import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "../../PermissionsProvider";
import { Workflow, WorkflowService } from "../../services/WorkflowService";
import { ApplicationService } from "../../services/ApplicationService";
import { Application } from "../../model_gen/application";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Loader2Icon, PlusIcon } from "lucide-react";
import NewApplicationModal from "../Application/NewApplicationModal";
import MonacoEditor from "@monaco-editor/react";
import { Switch } from "../ui/switch";
import { toast } from "sonner";
import { cn } from "../../lib/utils";
import WorkflowScriptEditor from "../shared/WorkflowScriptEditor";
import { TRANSFORM_TEMPLATE, DEFAULT_SCHEMA, MONACO_JSON_OPTIONS, MONACO_PYTHON_OPTIONS } from "./workflowConstants";

const slugify = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const WorkflowNew: React.FC = () => {
  const { apiKey } = usePermissions();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [refName, setRefName] = useState("");
  const [refNameEdited, setRefNameEdited] = useState(false);
  const [description, setDescription] = useState("");
  const [timeoutSecs, setTimeoutSecs] = useState(30);
  const [script, setScript] = useState("");
  const [scriptSubTab, setScriptSubTab] = useState("code");
  const [inputSchema, setInputSchema] = useState(DEFAULT_SCHEMA);
  const [outputSchema, setOutputSchema] = useState(DEFAULT_SCHEMA);
  const [transformSession, setTransformSession] = useState(TRANSFORM_TEMPLATE);
  const [transformEnabled, setTransformEnabled] = useState(false);
  const [creating, setCreating] = useState(false);
  const [existingWorkflows, setExistingWorkflows] = useState<Workflow[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [applicationId, setApplicationId] = useState<number | undefined>(undefined);
  const [showNewAppModal, setShowNewAppModal] = useState(false);

  useEffect(() => {
    if (!apiKey) return;
    const scriptSub = WorkflowService.getDefaultWorkflowScript(
      () => {},
      apiKey
    ).subscribe(
      (data: any) => {
        if (data?.code) setScript(data.code);
      },
      () => {}
    );
    const wfSub = WorkflowService.getWorkflows(() => {}, apiKey).subscribe(
      (data: any) => setExistingWorkflows(Array.isArray(data) ? data : []),
      () => {}
    );
    const appSub = ApplicationService.getApplications(() => {}, apiKey).subscribe(
      (response: any) => {
        const fetched = Array.isArray(response) ? response : (response.applications || []);
        setApplications(fetched);
      },
      () => {}
    );
    return () => {
      scriptSub.unsubscribe();
      wfSub.unsubscribe();
      appSub.unsubscribe();
    };
  }, [apiKey]);

  useEffect(() => {
    if (!refNameEdited) {
      setRefName(slugify(name));
    }
  }, [name, refNameEdited]);

  const handleCreate = () => {
    if (!apiKey || !name.trim() || !refName.trim() || !script.trim()) return;
    setCreating(true);
    const payload: Record<string, any> = {
      workflow_name: name.trim(),
      workflow_ref_name: refName.trim(),
      script,
      description: description.trim() || undefined,
      timeout_seconds: timeoutSecs,
      application_id: applicationId,
    };
    if (inputSchema.trim()) payload.input_schema = inputSchema;
    if (outputSchema.trim()) payload.output_schema = outputSchema;
    if (transformEnabled && transformSession.trim()) payload.transform_session = transformSession;
    payload.transform_enabled = transformEnabled;

    WorkflowService.createWorkflow(
      payload as any,
      () => {},
      apiKey
    ).subscribe(
      (workflow: any) => {
        setCreating(false);
        toast.success("Workflow created");
        navigate(`/forge/workflows/${workflow.id}`);
      },
      (err: any) => {
        setCreating(false);
        const msg =
          err?.error?.details?.join(", ") ||
          err?.error?.message ||
          err?.message ||
          "Unknown error";
        toast.error(`Failed to create: ${msg}`);
      }
    );
  };

  const refNameConflict = existingWorkflows.some(
    (w) => w.workflow_ref_name === refName.trim()
  );
  const canCreate =
    name.trim() && refName.trim() && script.trim() && !refNameConflict && applicationId;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-8 py-8 space-y-6">
        <Card className="border-border">
          <CardContent className="p-8">
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button
                    onClick={() => navigate("/")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Home
                  </button>
                </li>
                <li className="text-muted-foreground/40">/</li>
                <li>
                  <button
                    onClick={() => navigate("/forge")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Forge
                  </button>
                </li>
                <li className="text-muted-foreground/40">/</li>
                <li>
                  <button
                    onClick={() => navigate("/forge/workflows")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Workflows
                  </button>
                </li>
                <li className="text-muted-foreground/40">/</li>
                <li>
                  <span className="text-maitai-green font-gc-med">New</span>
                </li>
              </ol>
            </nav>

            <h1 className="text-3xl font-gc-demi-bold text-foreground tracking-tight">
              Create Workflow
            </h1>
            <p className="text-muted-foreground mt-1">
              Define a new workflow with custom Python logic
            </p>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="p-6 space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="wf-name">
                  Workflow Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="wf-name"
                  value={name}
                  maxLength={100}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="My Workflow"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wf-ref">
                  Ref Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="wf-ref"
                  value={refName}
                  maxLength={60}
                  onChange={(e) => {
                    setRefName(e.target.value);
                    setRefNameEdited(true);
                  }}
                  placeholder="my-workflow"
                />
                <p className="text-xs text-muted-foreground truncate">
                  Used in API calls: <code>model=&quot;workflow:{refName || "..."}&quot;</code>
                </p>
                {refNameConflict && (
                  <p className="text-xs text-red-600 dark:text-red-400">
                    A workflow with this ref name already exists
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="wf-desc">Description</Label>
              <Input
                id="wf-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Application <span className="text-red-500">*</span></Label>
                <Select
                  value={applicationId?.toString() || ""}
                  onValueChange={(val) => {
                    if (val === "new_application") {
                      setShowNewAppModal(true);
                    } else {
                      setApplicationId(val ? Number(val) : undefined);
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select an application" />
                  </SelectTrigger>
                  <SelectContent>
                    {applications
                      .filter((app) => app.id != null && !isNaN(app.id))
                      .map((app) => (
                        <SelectItem key={app.id} value={app.id!.toString()}>
                          {app.application_name}
                        </SelectItem>
                      ))}
                    <SelectItem value="new_application" className="border-t border-border mt-1 pt-2">
                      <div className="flex items-center gap-2 text-maitai-green font-medium">
                        <PlusIcon className="w-4 h-4" />
                        Create New Application
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="wf-timeout">Timeout (seconds)</Label>
                <Input
                  id="wf-timeout"
                  type="number"
                  min={1}
                  max={300}
                  value={timeoutSecs}
                  onChange={(e) => setTimeoutSecs(Number(e.target.value) || 30)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="p-6 space-y-4">
            <div className="border-b border-border">
              <nav className="flex space-x-6">
                {[
                  { key: "code", label: "Code" },
                  { key: "transform", label: "Transform Session" },
                  { key: "input_schema", label: "Input Schema" },
                  { key: "output_schema", label: "Output Schema" },
                ].map((sub) => (
                  <button
                    key={sub.key}
                    onClick={() => setScriptSubTab(sub.key)}
                    className={cn(
                      "px-3 py-2 text-sm font-gc-med rounded-t-md transition-colors",
                      scriptSubTab === sub.key
                        ? "bg-maitai-green/10 text-maitai-green"
                        : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                    )}
                  >
                    {sub.label}
                  </button>
                ))}
              </nav>
            </div>

            {scriptSubTab === "code" && (
              <WorkflowScriptEditor
                value={script}
                onChange={setScript}
                minHeight={400}
              />
            )}

            {scriptSubTab === "input_schema" && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Define the expected input structure as a JSON object. This will be injected as <code>INPUT_SCHEMA</code> in the workflow file.
                </p>
                <div className="rounded-lg border border-border overflow-hidden">
                  <MonacoEditor
                    height={300}
                    language="json"
                    theme="vs-dark"
                    value={inputSchema}
                    onChange={(val) => setInputSchema(val || "")}
                    options={MONACO_JSON_OPTIONS}
                  />
                </div>
              </div>
            )}

            {scriptSubTab === "output_schema" && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Define the expected output structure as a JSON object. This will be injected as <code>OUTPUT_SCHEMA</code> in the workflow file.
                </p>
                <div className="rounded-lg border border-border overflow-hidden">
                  <MonacoEditor
                    height={300}
                    language="json"
                    theme="vs-dark"
                    value={outputSchema}
                    onChange={(val) => setOutputSchema(val || "")}
                    options={MONACO_JSON_OPTIONS}
                  />
                </div>
              </div>
            )}

            {scriptSubTab === "transform" && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">
                    Define a <code>transform_session(session)</code> function to convert sessions into workflow input/output for test set creation.
                  </p>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-muted-foreground">
                      {transformEnabled ? "Enabled" : "Disabled"}
                    </label>
                    <Switch
                      checked={transformEnabled}
                      onCheckedChange={setTransformEnabled}
                    />
                  </div>
                </div>
                <div className={cn("rounded-lg border border-border overflow-hidden", !transformEnabled && "opacity-50 pointer-events-none")}>
                  <MonacoEditor
                    height={400}
                    language="python"
                    theme="vs-dark"
                    value={transformSession}
                    onChange={(val) => setTransformSession(val || "")}
                    options={MONACO_PYTHON_OPTIONS}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-3">
          <Button
            variant="ghost"
            onClick={() => navigate("/forge/workflows")}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={creating || !canCreate}
            className="gap-2"
          >
            {creating ? (
              <Loader2Icon className="w-4 h-4 animate-spin" />
            ) : (
              <PlusIcon className="w-4 h-4" />
            )}
            Create Workflow
          </Button>
        </div>

        <NewApplicationModal
          isOpen={showNewAppModal}
          onClose={() => setShowNewAppModal(false)}
          onApplicationCreated={(newApp: Application) => {
            setApplications((prev) => [...prev, newApp]);
            setApplicationId(newApp.id);
          }}
        />
      </div>
    </div>
  );
};

export default WorkflowNew;
